import { cn } from "@dl/web-core-js/ui";
import type { TFunction } from "i18next";
import { Check, Circle, CircleDot, Minus } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { ProductionOrderComponentDTO, ProductionOrderOperationDTO } from "../../api/floor";
import { formatNumber } from "../../api/manufacturing";

/** The plant's processes in floor order; anything else sorts after them by code. */
export const PROCESS_ORDER = ["CUT", "EDGE", "MACHINING", "PAINT", "ASSEMBLY", "INSPECTION", "PACK"];

export function processRank(code: string | null | undefined): number {
  const index = PROCESS_ORDER.indexOf(code ?? "");
  return index < 0 ? PROCESS_ORDER.length : index;
}

/** A process as the floor says it, in the screen's language. */
export function processLabel(t: TFunction, code: string | null | undefined, fallback?: string | null): string {
  switch (code) {
    case "CUT":
      return t("floor.process.cut", "Cut");
    case "EDGE":
      return t("floor.process.edge", "Edge");
    case "MACHINING":
      return t("floor.process.machining", "Machining");
    case "PAINT":
      return t("floor.process.paint", "Paint");
    case "ASSEMBLY":
      return t("floor.process.assembly", "Assembly");
    case "INSPECTION":
      return t("floor.process.inspection", "Inspection");
    case "PACK":
      return t("floor.process.pack", "Packing");
    default:
      return fallback ?? code ?? "";
  }
}

/**
 * Parts per counted unit at a step: a nested part's Cut and Edge count
 * cutboards, so one counted unit holds several parts.
 */
export function partsPerUnit(step: ProductionOrderOperationDTO, component?: ProductionOrderComponentDTO | null): number {
  if (step.counting_unit !== "CUTBOARD") return 1;
  const nesting = component?.parts_per_cutboard ?? null;
  if (nesting && nesting > 1) return nesting;
  const required = Number(step.quantity_required ?? 0);
  const counted = Number(step.quantity_required_counted ?? required);
  return counted > 0 && required > counted ? required / counted : 1;
}

/** Good parts done at a step, converted to parts. */
export function doneParts(step: ProductionOrderOperationDTO, component?: ProductionOrderComponentDTO | null): number {
  return Number(step.quantity_done ?? 0) * partsPerUnit(step, component);
}

/** A step is waiting at its process when it is open and the step before has handed it more than it has done. */
export function isWaiting(step: ProductionOrderOperationDTO, component?: ProductionOrderComponentDTO | null): boolean {
  if (step.status !== "PENDING" && step.status !== "ACTIVE") return false;
  return Number(step.quantity_available ?? 0) > doneParts(step, component) + 0.0001;
}

/** Steps of a component (or lot) in routing order. */
/**
 * A component's steps in the order the work flows: by how many steps stand
 * before each (what it waits for, and what those wait for), then by position.
 * A step added late to a routing (Paint at position 10) still shows between
 * Machining and Inspection; steps side by side (Edge, Machining) keep their
 * positions' order.
 */
export function byPosition(steps: ProductionOrderOperationDTO[]): ProductionOrderOperationDTO[] {
  const byId = new Map(steps.map((step) => [step.id, step]));
  const depth = new Map<number, number>();
  const depthOf = (step: ProductionOrderOperationDTO, seen: Set<number>): number => {
    const known = depth.get(step.id);
    if (known !== undefined) return known;
    if (seen.has(step.id)) return 0; // a loop the server refuses anyway
    seen.add(step.id);
    const before = (step.depends_on_ids ?? []).map((id) => byId.get(id)).filter((other) => other !== undefined);
    const value = before.length === 0 ? 0 : 1 + Math.max(...before.map((other) => depthOf(other, seen)));
    depth.set(step.id, value);
    return value;
  };
  return [...steps].sort((a, b) => depthOf(a, new Set()) - depthOf(b, new Set()) || a.position - b.position);
}

/**
 * One step as a chip: done green with a check, in progress blue with done over
 * required, to do grey, skipped struck through; stalled amber, missing red with
 * the count. The colour is never the only cue: each state has its own icon.
 */
export function StepChip({ step, large = false, showName = true }: { step: ProductionOrderOperationDTO; large?: boolean; showName?: boolean }) {
  const { t } = useTranslation();
  const missing = Number(step.quantity_missing ?? 0);
  const done = Number(step.quantity_done ?? 0);
  const required = Number(step.quantity_required_counted ?? step.quantity_required);
  const tone =
    step.status === "DONE"
      ? "border-ok/40 bg-ok-soft text-ok"
      : step.status === "SKIPPED"
        ? "border-line bg-surface-sunk text-ink-faint line-through"
        : missing > 0
          ? "border-risk/50 bg-risk-soft text-risk"
          : step.is_stalled
            ? "border-warn/50 bg-warn-soft text-warn"
            : step.status === "ACTIVE"
              ? "border-info/40 bg-info-soft text-info"
              : "border-line bg-surface text-ink-muted";
  const Icon = step.status === "DONE" ? Check : step.status === "SKIPPED" ? Minus : step.status === "ACTIVE" ? CircleDot : Circle;
  const name = processLabel(t, step.process_code, step.process_name);
  const title = [
    name,
    `${formatNumber(done)} / ${formatNumber(required)}${step.counting_unit === "CUTBOARD" && required !== Number(step.quantity_required) ? ` ${t("floor.cutboards-short", "boards")}` : ""}`,
    missing > 0 ? t("floor.missing-n", "{{n}} missing", { n: formatNumber(missing) }) : null,
    step.is_stalled ? t("floor.stalled", "Stalled") : null,
    step.station_codes ?? null,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <span
      title={title}
      className={cn(
        "inline-flex items-center gap-1 whitespace-nowrap rounded-full border font-medium tabular-nums",
        large ? "px-3 py-1.5 text-[15px]" : "px-2 py-0.5 text-[11px]",
        tone,
      )}
    >
      <Icon className={large ? "size-4" : "size-3"} aria-hidden />
      {showName && <span>{name}</span>}
      {step.status === "ACTIVE" && (
        <span>
          {formatNumber(done)}/{formatNumber(required)}
        </span>
      )}
      {missing > 0 && step.status !== "DONE" && <span>−{formatNumber(missing)}</span>}
    </span>
  );
}
